import {
    GoogleBooksSearchResponseSchema,
    type GoogleBooksVolume,
    type TransformedGoogleBook,
} from "@/features/books/types/googleBooks";
import logger from "@/logger";

const GOOGLE_BOOKS_API_BASE = "https://www.googleapis.com/books/v1/volumes";
const RETRYABLE_STATUS_CODES = new Set([429, 503]);
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 300;

/**
 * Gets the Google Books API key from environment variables
 */
function getApiKey(): string | undefined {
    return import.meta.env.VITE_GOOGLE_BOOKS_API_KEY;
}

/**
 * Fetches a URL, retrying on transient Google Books API failures (503/429).
 */
async function fetchGoogleBooksWithRetry(url: string): Promise<Response> {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        const response = await fetch(url);

        if (response.ok) {
            return response;
        }

        const errorText = await response.text();
        logger.error(`[GoogleBooks] API error: ${response.status} ${response.statusText}`, errorText);

        const shouldRetry = RETRYABLE_STATUS_CODES.has(response.status) && attempt < MAX_ATTEMPTS;
        if (!shouldRetry) {
            throw new Error(`Google Books API error: ${response.status} ${response.statusText}`);
        }

        const delayMs = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
        logger.warn(
            `[GoogleBooks] Retrying after ${response.status} (attempt ${attempt}/${MAX_ATTEMPTS}) in ${delayMs}ms`
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    // Unreachable: loop always returns or throws
    throw new Error("Google Books API request failed");
}

/**
 * Transforms a Google Books volume to our internal format
 */
function transformVolume(volume: GoogleBooksVolume): TransformedGoogleBook {
    const { volumeInfo } = volume;

    // Extract ISBN (prefer ISBN_13, fallback to ISBN_10)
    let isbn: string | undefined;
    if (volumeInfo.industryIdentifiers) {
        const isbn13 = volumeInfo.industryIdentifiers.find((id) => id.type === "ISBN_13");
        const isbn10 = volumeInfo.industryIdentifiers.find((id) => id.type === "ISBN_10");
        isbn = isbn13?.identifier ?? isbn10?.identifier;
    }

    // Extract cover URL (prefer thumbnail, fallback to smallThumbnail)
    // Google Books API returns http:// URLs which are blocked by iOS ATS
    const rawCoverUrl =
        volumeInfo.imageLinks?.thumbnail ??
        volumeInfo.imageLinks?.smallThumbnail ??
        volumeInfo.imageLinks?.medium ??
        volumeInfo.imageLinks?.large;
    const coverUrl = rawCoverUrl?.replace(/^http:\/\//, "https://");

    // Extract author (join multiple authors with comma)
    const author = volumeInfo.authors?.join(", ") ?? "Unknown Author";

    return {
        author,
        coverUrl,
        description: volumeInfo.description,
        isbn,
        publishedDate: volumeInfo.publishedDate,
        publisher: volumeInfo.publisher,
        title: volumeInfo.title,
    };
}

/**
 * Searches Google Books API
 */
async function searchBooks(query: string, searchType: "title" | "isbn"): Promise<TransformedGoogleBook[]> {
    logger.debug(`[GoogleBooks] Searching for ${searchType}: "${query}"`);

    if (!query.trim()) {
        return [];
    }

    const apiKey = getApiKey();
    let url = `${GOOGLE_BOOKS_API_BASE}?q=`;

    // Build query based on search type
    if (searchType === "isbn") {
        // Remove hyphens and spaces from ISBN
        const cleanIsbn = query.replace(/[-\s]/g, "");
        url += `isbn:${encodeURIComponent(cleanIsbn)}`;
    } else {
        url += `intitle:${encodeURIComponent(query)}`;
    }

    // Add API key if available
    if (apiKey) {
        url += `&key=${encodeURIComponent(apiKey)}`;
    }

    // Add maxResults to limit response size
    url += "&maxResults=40";

    // Restrict to English language books
    url += "&langRestrict=en";

    const response = await fetchGoogleBooksWithRetry(url);
    const rawData = await response.json();
    const parseResult = GoogleBooksSearchResponseSchema.safeParse(rawData);

    if (!parseResult.success) {
        logger.error(`[GoogleBooks] Invalid API response:`, parseResult.error);
        throw new Error("Invalid response from Google Books API");
    }

    const data = parseResult.data;

    if (!data.items || data.items.length === 0) {
        logger.debug(`[GoogleBooks] No results found for ${searchType}: "${query}"`);
        return [];
    }

    const transformed = data.items.map(transformVolume);
    logger.debug(`[GoogleBooks] Found ${transformed.length} results for ${searchType}: "${query}"`);

    return transformed;
}

/**
 * Gets a book by ISBN
 */
async function getBookByIsbn(isbn: string): Promise<TransformedGoogleBook | null> {
    logger.debug(`[GoogleBooks] Getting book by ISBN: "${isbn}"`);

    const results = await searchBooks(isbn, "isbn");

    if (results.length === 0) {
        return null;
    }

    // Return first result (ISBN search should be unique)
    return results[0];
}

/**
 * Gets books by title
 */
async function getBookByTitle(title: string): Promise<TransformedGoogleBook[]> {
    logger.debug(`[GoogleBooks] Getting books by title: "${title}"`);

    return searchBooks(title, "title");
}

const googleBooksService = {
    getBookByIsbn,
    getBookByTitle,
    searchBooks,
};

export default googleBooksService;
